import type { ChatSessionFragment } from "@qw/graphql";

/**
 * Whether the viewer may only read this chat session — the gate behind `ChatFooter`'s read-only
 * alert, the elicitation-form render in `chat-session-message-tool-invocation.tsx`, and
 * `useMcpChat`'s send/regenerate no-ops. `ChatProvider` derives `isReadOnly` from it once and
 * publishes that on the chat context; nothing downstream re-derives it.
 *
 * The answer is the server's. `ChatSession.viewerCanSendMessages` resolves
 * `userCanWriteToChatSession` — the same predicate `ChatController.validateSession` runs on all four
 * write endpoints — so the composer this decides to render matches what a send would actually be
 * admitted to do. The client used to guess with `session.userId !== viewer`, which is wrong for a
 * conversation-anchored session: that row is shared across everyone holding a copy of the thread and
 * `userId` records only whoever opened it first, so a deal collaborator got the read-only alert
 * instead of a composer (B-1726). Sessions with no conversation anchor — book chat, insured chat,
 * playbook/trigger sessions — are unaffected: the server short-circuits those to that very
 * comparison, so they read exactly as they always did.
 *
 * The field is `Boolean!` and every session reaching here comes from a document that spreads the
 * `ChatSession` fragment, so it is always present in practice. It is read as optional anyway —
 * belt-and-braces at a public `@qw/chat` boundary, since `ChatProvider` takes the session as a prop
 * from web and inbox. A session that somehow arrives without the field keeps its previous behavior
 * instead of going silently read-only.
 */
export const isChatSessionReadOnly = (
  chatSession: Pick<ChatSessionFragment, "userId"> & { viewerCanSendMessages?: boolean | null },
  currentUserId: string | null | undefined
): boolean => {
  const { userId, viewerCanSendMessages } = chatSession;

  return !(viewerCanSendMessages ?? userId === currentUserId);
};
